import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import { ReportActions } from '../../../components/ui/ReportActions';
import { Multiselect } from '../../../components/ui/Multiselect/Multiselect';
import usePaymentReport from './usePaymentReport';
import Container from '../../../components/ui/Container/Container';
import { Row, Col, Form, Button, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Helpers } from '../../../utils/helpers';
import { IKeyValuePair } from '../../../utils/constants/interfaces';
import styles from './PaymentReport.module.css';

interface PaymentReportProps {
  visible?: boolean;
}

export const PaymentReport = ({ visible = true }: PaymentReportProps) => {
  const manager = usePaymentReport();
  const handleClearFilters = () => {
    if (manager.resetSearch) {
      manager.resetSearch();
    }
  };

  const handleSearch = () => {
    manager.triggerSearch();
  };

  // Helper function to convert Date to YYYY-MM-DD string for date inputs
  const dateToInputValue = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  // Generate payment method options from system codes
  const paymentMethodOptions: IKeyValuePair<string>[] = manager.paymentMethodOptions.map(
    (paymentMethod) => ({
      key: paymentMethod.description || paymentMethod.codeValue,
      value: paymentMethod.description || paymentMethod.codeValue
    })
  );

  const handlePaymentMethodsChange = (selectedValues: string[]) => {
    // Convert selected display names back to IDs
    const paymentMethodIds = selectedValues
      .map((displayName) => {
        const method = manager.paymentMethodOptions.find(
          (opt) => (opt.description || opt.codeValue) === displayName
        );
        return method?.id;
      })
      .filter((id): id is number => id !== undefined);

    manager.updateFilter('paymentMethodIds', paymentMethodIds);
  };

  // Get selected payment method display names for the multiselect
  const getSelectedPaymentMethodNames = (): string[] => {
    return manager.searchParameters.paymentMethodIds.map((id) => {
      const method = manager.paymentMethodOptions.find((opt) => opt.id === id);
      return method?.description || method?.codeValue || 'Unknown';
    });
  };

  if (!visible) return <></>;

  return (
    <Container visible={true} className={styles.reportContainer}>
      {/* Make the form wider to accommodate more fields per row */}
      <Row className="justify-content-center">
        <Col xl={10} lg={11} md={12}>
          <div className={styles.filterSection}>
            {/* Filter Header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Payment Report</h5>
            </div>

            {/* Row 1: Payment Date (From), Payment Date (Thru), CM ID, First Name, Last Name */}
            <Row className="g-2 mb-2">
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Payment Date (From)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.paymentDateFrom)}
                    onChange={(e) => manager.updateFilter('paymentDateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Payment Date (Thru)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.paymentDateThru)}
                    onChange={(e) => manager.updateFilter('paymentDateThru', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>CM ID</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={manager.searchParameters.clubMemberId || ''}
                    onChange={(e) => manager.updateFilter('clubMemberId', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>First Name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={manager.searchParameters.firstName}
                    onChange={(e) => manager.updateFilter('firstName', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Last Name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={manager.searchParameters.lastName}
                    onChange={(e) => manager.updateFilter('lastName', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 2: First Joined (From), First Joined (Thru), Billing Year, Order Number, Transaction ID */}
            <Row className="g-2 mb-2">
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>First Joined (From)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.firstJoinedDateFrom)}
                    onChange={(e) => manager.updateFilter('firstJoinedDateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>First Joined (Thru)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.firstJoinedDateThru)}
                    onChange={(e) => manager.updateFilter('firstJoinedDateThru', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Billing Year</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={manager.searchParameters.billingYear || ''}
                    onChange={(e) => manager.updateFilter('billingYear', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Order Number</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={manager.searchParameters.orderNumber}
                    onChange={(e) => manager.updateFilter('orderNumber', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Transaction ID</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={manager.searchParameters.transactionId}
                    onChange={(e) => manager.updateFilter('transactionId', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 3: Order ID, Payment Methods multiselect */}
            <Row className="g-2 mb-3">
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Order ID</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={manager.searchParameters.orderId}
                    onChange={(e) => manager.updateFilter('orderId', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Payment Methods</Form.Label>
                  <Multiselect
                    options={paymentMethodOptions}
                    selectedOptions={getSelectedPaymentMethodNames()}
                    onChange={handlePaymentMethodsChange}
                    size="small"
                  />
                  {manager.loadingPaymentMethods && (
                    <small className="text-muted">Loading payment methods...</small>
                  )}
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={!manager.hasSearchParameters() || manager.loading}>
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    Search
                  </Button>
                  <Button variant="outline-secondary" onClick={handleClearFilters}>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Clear All
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Results Section */}
          <div className={styles.resultsSection}>
            <LoadingContainer isLoading={manager.loading}>
              {!manager.hasSearchParameters() && (
                <div className="text-center py-4 text-muted">
                  <p>
                    Please enter search criteria above and click &ldquo;Search&rdquo; to view
                    payments.
                  </p>
                </div>
              )}
              {manager.hasSearchParameters() && manager.paymentPagedResults.resultCount === 0 && (
                <div className="text-center py-4 text-muted">
                  No payments found matching your criteria.
                </div>
              )}
              {manager.paymentPagedResults.resultCount > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>{manager.paymentPagedResults.resultCount}</strong> payments found
                    </div>
                    <ReportActions
                      data={manager.allResults}
                      columns={manager.getPrintColumns()}
                      csvColumns={manager.getExportColumns()}
                      reportTitle="Payment Report"
                      csvFileName="paymentReportExport.csv"
                      searchCriteria={manager.getSearchCriteria()}
                      footerHtml={manager.getReportFooterHtml()}
                      size="sm"
                      variant="outline-primary"
                    />
                  </div>

                  <Table responsive hover className={styles.resultsTable}>
                    <thead>
                      <tr>
                        <SortableHeader
                          text="Payment ID"
                          sortField="id"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'id'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="CM ID"
                          sortField="clubMemberId"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'clubMemberId'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="First Name"
                          sortField="firstName"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'firstName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Last Name"
                          sortField="lastName"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'lastName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Billing Year"
                          sortField="billingYear"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'billingYear'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Payment Date"
                          sortField="paymentDateUtc"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'paymentDateUtc'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Payment Method"
                          sortField="paymentMethodId"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'paymentMethodId'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Amount"
                          sortField="amount"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'amount'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Net"
                          sortField="net"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'net'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Balance"
                          sortField="remainingBalance"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'remainingBalance'}
                          onClick={manager.changeSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {manager.paymentPagedResults.results?.map((payment) => {
                        // CC Fees default to $0.00 for now
                        const ccFees = 0.0;
                        const netAmount = payment.amount - ccFees;

                        return (
                          <tr
                            key={payment.id}
                            className={styles.clickableRow}
                            onClick={() => window.open(`/members/${payment.clubMemberId}`, '_blank')}>
                            <td>{payment.id}</td>
                            <td>{payment.clubMemberId}</td>
                            <td>{payment.firstName || 'N/A'}</td>
                            <td>{payment.lastName || 'N/A'}</td>
                            <td>{payment.billingYear}</td>
                            <td>{Helpers.toShortDateFromUtc(payment.paymentDateUtc)}</td>
                            <td>
                              {payment.paymentMethod?.description ||
                                payment.paymentMethod?.codeValue ||
                                'Unknown'}
                            </td>
                            <td>{Helpers.formatCurrency(payment.amount)}</td>
                            <td>{Helpers.formatCurrency(netAmount)}</td>
                            <td style={{ color: payment.remainingBalance > 0 ? 'red' : 'inherit' }}>
                              {Helpers.formatCurrency(payment.remainingBalance)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  <div className="pt-4">
                    <PagingControls
                      page={manager.paymentPagedResults.currentPage ?? 1}
                      pageCount={manager.paymentPagedResults.pageCount ?? 1}
                      maxPlusOrMinusPagesToShow={3}
                      onPageChange={manager.changePage}
                    />
                  </div>

                  {/* Report Totals Table */}
                  <div className="d-flex justify-content-end mt-4">
                    <div className={styles.reportTotalsContainer}>
                      <h6 className="mb-3">Report Totals:</h6>
                      <Table size="sm" bordered className={styles.reportTotalsTable}>
                        <thead>
                          <tr>
                            <th>Payment Method</th>
                            <th>Payment Records</th>
                            <th>Membership Count</th>
                            <th>Total Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manager.getPaymentMethodTotals().map((total) => (
                            <tr key={total.paymentMethod}>
                              <td>{total.paymentMethod}</td>
                              <td className={styles.centerAlign}>{total.count}</td>
                              <td className={styles.centerAlign}>{total.membershipCount}</td>
                              <td className={styles.rightAlign}>
                                {Helpers.formatCurrency(total.amount)}
                              </td>
                            </tr>
                          ))}
                          <tr className={styles.totalRow}>
                            <td>Grand Total</td>
                            <td className={styles.centerAlign}>{manager.allResults.length}</td>
                            <td className={styles.centerAlign}>
                              {new Set(manager.allResults.map((p) => p.clubMemberId)).size}
                            </td>
                            <td className={styles.rightAlign}>
                              {Helpers.formatCurrency(
                                manager.allResults.reduce((sum, payment) => sum + payment.amount, 0)
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </LoadingContainer>
          </div>
        </Col>
      </Row>
    </Container>
  );
};
